import { ArrowDown, ArrowRight, ArrowUp, Check, Coins, Trophy, X } from "lucide-react";
import { MasteryBar } from "@/components/States";
import { Button } from "@/components/ui/button";
import { studentCopy } from "@/lib/studentI18n";

const LETTERS = ["A", "B", "C", "D"];

export function FullScreen({ children }) {
  return <div className="focus-mode flex min-h-screen items-start justify-center px-4 py-8 sm:py-14">{children}</div>;
}

export function StudySessionView({ study, onExit, language = "es" }) {
  const copy = studentCopy(language);
  const { session, items, index, feedback, busy, resumed, current, answerPayload, error } = study;
  const progress = Math.round(((index + (feedback ? 1 : 0)) / items.length) * 100);
  return (
    <div className="mx-auto w-full max-w-2xl" data-testid="study-mode">
      <StudyHeader index={index} total={items.length} progress={progress} onExit={onExit} copy={copy} />
      {resumed && <ResumedNotice onRestart={() => study.begin(session.mode, Math.min(session.total_questions, 40), true)} copy={copy} />}
      <QuestionPanel study={study} answerPayload={answerPayload} busy={busy} error={error} copy={copy} />
    </div>
  );
}

function StudyHeader({ index, total, progress, onExit, copy }) {
  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <button onClick={onExit} className="text-sm font-semibold text-slate-400 hover:text-slate-600" data-testid="study-exit-btn">{copy.logout}</button>
        <span className="text-sm font-semibold text-slate-500" data-testid="study-progress-label">{index + 1} {copy.of} {total}</span>
      </div>
      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-blue-600 transition-[width] duration-500" style={{ width: `${progress}%` }} data-testid="study-progress-bar" />
      </div>
    </>
  );
}

function ResumedNotice({ onRestart, copy }) {
  return (
    <div className="mt-5 flex flex-col gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950 sm:flex-row sm:items-center sm:justify-between" data-testid="study-resumed-notice">
      <p>{copy.resumed}</p>
      <button type="button" onClick={onRestart} className="shrink-0 font-bold text-blue-700 hover:text-blue-900" data-testid="study-restart-btn">{copy.restart}</button>
    </div>
  );
}

function QuestionPanel({ study, answerPayload, busy, error, copy }) {
  const { current, index, items, feedback } = study;
  return (
    <div className="mt-10" key={current.question.id}>
      <p className="font-display text-2xl font-bold leading-snug text-slate-900 sm:text-3xl" data-testid="question-prompt">{current.question.prompt}</p>
      <div className="mt-8 space-y-3"><QuestionInput study={study} copy={copy} /></div>
      {feedback && <AnswerFeedback feedback={feedback} copy={copy} />}
      <div className="mt-8">
        {feedback ? (
          <Button onClick={study.next} disabled={busy} className="estudia-touch w-full rounded-2xl bg-slate-900 text-base font-bold hover:bg-slate-800" data-testid="next-question-btn">
            {index + 1 < items.length ? copy.next : copy.results}<ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button onClick={study.submit} disabled={!answerPayload || busy} className="estudia-touch w-full rounded-2xl bg-blue-600 text-base font-bold hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400" data-testid="submit-answer-btn">{copy.answer}</Button>
        )}
      </div>
      {error && <p className="mt-4 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="study-error">{error}</p>}
    </div>
  );
}

function QuestionInput({ study, copy }) {
  const { current, choice, setChoice, feedback } = study;
  const question = current.question;
  if (question.type === "multiple_choice") {
    return (question.payload.options || []).map((option, index) => (
      <OptionButton
        key={option}
        label={LETTERS[index]}
        text={option}
        selected={choice === index}
        state={optionState(index, choice, feedback, feedback?.correct_answer?.correct_index)}
        disabled={!!feedback}
        onClick={() => setChoice(index)}
        testId={`option-${index}`}
      />
    ));
  }
  if (question.type === "true_false") {
    return [copy.false, copy.true].map((text, index) => (
      <OptionButton
        key={text}
        label={index === 1 ? copy.true[0] : copy.false[0]}
        text={text}
        selected={choice === index}
        state={optionState(index, choice, feedback, Number(feedback?.correct_answer?.value))}
        disabled={!!feedback}
        onClick={() => setChoice(index)}
        testId={`option-${index}`}
      />
    ));
  }
  if (question.type === "matching") {
    return (
      <Matching
        lefts={question.payload.lefts || []}
        rights={question.payload.rights || []}
        pairs={study.pairs}
        activeLeft={study.activeLeft}
        setActiveLeft={study.setActiveLeft}
        onPair={(left, right) => study.setPairs((previous) => ({ ...previous, [left]: right }))}
        onClear={(left) => study.setPairs((previous) => withoutKey(previous, left))}
        feedback={feedback}
        disabled={!!feedback}
        copy={copy}
      />
    );
  }
  if (question.type === "fill_blank") {
    return <FillBlank sentence={question.payload.sentence} value={study.textAnswer} onChange={study.setTextAnswer} feedback={feedback} copy={copy} />;
  }
  if (question.type === "ordering") {
    return <Ordering items={study.orderedItems} onChange={study.setOrderedItems} feedback={feedback} copy={copy} />;
  }
  return null;
}

function optionState(index, choice, feedback, correctIndex) {
  if (!feedback) return null;
  if (index === correctIndex) return "correct";
  return choice === index ? "incorrect" : null;
}

function withoutKey(values, key) {
  const next = { ...values };
  delete next[key];
  return next;
}

function AnswerFeedback({ feedback, copy }) {
  return (
    <div className={`animate-rise mt-7 rounded-2xl p-4 ${feedback.is_correct ? "bg-emerald-50 text-emerald-900" : "bg-rose-50 text-rose-900"}`} data-testid="answer-feedback">
      <p className="flex items-center gap-2 font-display text-base font-bold">
        {feedback.is_correct ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
        {feedback.is_correct ? copy.correct : copy.incorrect}
      </p>
      {feedback.explanation && <p className="mt-1.5 text-sm">{feedback.explanation}</p>}
    </div>
  );
}

function OptionButton({ label, text, selected, state, disabled, onClick, testId }) {
  const base = "flex estudia-touch w-full items-center gap-4 rounded-2xl border-2 px-4 py-3 text-left text-base font-semibold shadow-sm transition-all duration-200";
  const tone = state === "correct"
    ? "border-emerald-500 bg-emerald-50 text-emerald-900"
    : state === "incorrect"
      ? "border-rose-400 bg-rose-50 text-rose-900"
      : selected
        ? "border-blue-500 bg-blue-50 text-blue-900"
        : "border-slate-200 bg-white text-slate-800 hover:-translate-y-0.5 hover:border-violet-300 hover:shadow-md";
  return (
    <button className={`${base} ${tone}`} onClick={onClick} disabled={disabled} data-testid={testId}>
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-white/80 font-display text-sm font-bold">{label}</span>
      <span className="min-w-0">{text}</span>
    </button>
  );
}

function Matching({ lefts, rights, pairs, activeLeft, setActiveLeft, onPair, onClear, feedback, disabled, copy }) {
  const used = new Set(Object.values(pairs));
  const correctMap = feedback ? Object.fromEntries((feedback.correct_answer.pairs || []).map((pair) => [pair.left, pair.right])) : null;
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="matching-question">
      <div className="space-y-2">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{copy.concepts}</p>
        {lefts.map((left, index) => <MatchingLeft key={left} left={left} index={index} matched={pairs[left]} correct={correctMap?.[left]} active={activeLeft === left} disabled={disabled} onSelect={() => (pairs[left] && !disabled ? onClear(left) : setActiveLeft(left))} />)}
      </div>
      <div className="space-y-2">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{copy.definitions}</p>
        {rights.map((right, index) => (
          <button key={right} onClick={() => { if (activeLeft) { onPair(activeLeft, right); setActiveLeft(null); } }} disabled={disabled || (used.has(right) && pairs[activeLeft] !== right)} className={`estudia-touch w-full rounded-2xl border-2 px-4 py-3 text-left text-sm transition-colors ${used.has(right) ? "border-slate-200 bg-slate-100 text-slate-400" : activeLeft ? "border-blue-300 bg-white text-slate-800 hover:border-blue-500" : "border-slate-200 bg-white text-slate-800"}`} data-testid={`matching-right-${index}`}>{right}</button>
        ))}
        {!activeLeft && !disabled && <p className="pt-1 text-xs text-slate-400">{copy.matchingHint}</p>}
      </div>
    </div>
  );
}

function MatchingLeft({ left, index, matched, correct, active, disabled, onSelect }) {
  const hasFeedback = correct !== undefined;
  const isCorrect = hasFeedback ? correct === matched : null;
  const tone = hasFeedback
    ? isCorrect ? "border-emerald-500 bg-emerald-50 text-emerald-900" : "border-rose-400 bg-rose-50 text-rose-900"
    : active ? "border-blue-500 bg-blue-50" : matched ? "border-slate-300 bg-slate-50" : "border-slate-200 bg-white";
  return (
    <button onClick={onSelect} disabled={disabled} className={`estudia-touch w-full rounded-2xl border-2 px-4 py-3 text-left text-sm font-semibold transition-colors ${tone}`} data-testid={`matching-left-${index}`}>
      <span className="block text-slate-900">{left}</span>
      {matched && <span className="mt-1 block text-xs font-medium text-slate-500">→ {matched}</span>}
      {hasFeedback && !isCorrect && <span className="mt-1 block text-xs font-medium text-emerald-700">✓ {correct}</span>}
    </button>
  );
}

function FillBlank({ sentence, value, onChange, feedback, copy }) {
  const [before, after] = String(sentence || "").split("___");
  return (
    <div className="estudia-card p-5" data-testid="fill-blank-question">
      <p className="text-lg font-semibold leading-relaxed text-slate-800">{before}<span className="mx-1 inline-block min-w-24 border-b-2 border-blue-400 text-center text-blue-700">{feedback ? value : "?"}</span>{after}</p>
      <label className="mt-5 block text-sm font-bold text-slate-600" htmlFor="fill-blank-answer">{copy.yourAnswer}</label>
      <textarea
        id="fill-blank-answer"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        maxLength={200}
        rows={2}
        disabled={!!feedback}
        autoComplete="off"
        autoCapitalize="sentences"
        enterKeyHint="done"
        inputMode="text"
        className="mt-2 min-h-16 w-full resize-y rounded-xl border-2 border-slate-200 px-4 py-3 text-base leading-relaxed outline-none focus:border-blue-500 disabled:bg-slate-50"
        data-testid="fill-blank-input"
      />
      {feedback && !feedback.is_correct && <p className="mt-3 text-sm font-semibold text-emerald-700">{copy.expectedAnswer}: {(feedback.correct_answer.answers || []).join(" / ")}</p>}
    </div>
  );
}

function Ordering({ items, onChange, feedback, copy }) {
  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid="ordering-question">
      <p className="text-sm text-slate-500">{copy.orderingHint}</p>
      {items.map((item, index) => (
        <div key={item} className="estudia-card flex min-h-16 items-center gap-3 p-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-blue-50 font-display font-bold text-blue-700">{index + 1}</span>
          <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800">{item}</span>
          {!feedback && <OrderButtons item={item} index={index} lastIndex={items.length - 1} onMove={move} copy={copy} />}
        </div>
      ))}
      {feedback && !feedback.is_correct && (
        <div className="rounded-2xl bg-emerald-50 p-4 text-sm text-emerald-900">
          <p className="font-bold">{copy.correctOrder}</p>
          <ol className="mt-2 list-inside list-decimal space-y-1">{(feedback.correct_answer.items || []).map((item) => <li key={item}>{item}</li>)}</ol>
        </div>
      )}
    </div>
  );
}

function OrderButtons({ item, index, lastIndex, onMove, copy }) {
  return (
    <div className="flex shrink-0 gap-1">
      <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} aria-label={`${copy.moveUp} ${item}`} className="grid h-10 w-10 place-items-center rounded-xl text-slate-600 hover:bg-slate-100 disabled:opacity-30"><ArrowUp className="h-5 w-5" /></button>
      <button type="button" onClick={() => onMove(index, 1)} disabled={index === lastIndex} aria-label={`${copy.moveDown} ${item}`} className="grid h-10 w-10 place-items-center rounded-xl text-slate-600 hover:bg-slate-100 disabled:opacity-30"><ArrowDown className="h-5 w-5" /></button>
    </div>
  );
}

export function Results({ results, onExtend, busy, error, onExit, language = "es" }) {
  const copy = studentCopy(language);
  const { session, by_topic: byTopic, gaming_mode: gaming, coin_reasons: reasons, coins_balance: balance } = results;
  const accuracy = session.accuracy === null ? 0 : Math.round(session.accuracy * 100);
  return (
    <div className="mx-auto w-full max-w-2xl" data-testid="session-results">
      <ResultsSummary session={session} accuracy={accuracy} gaming={gaming} reasons={reasons} balance={balance} copy={copy} />
      {byTopic.length > 0 && <TopicResults topics={byTopic} copy={copy} />}
      <ExtendActions onExtend={onExtend} busy={busy} copy={copy} />
      {error && <p className="mt-4 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="results-error">{error}</p>}
      <Button onClick={onExit} className="estudia-touch mt-4 w-full rounded-2xl bg-blue-600 text-base font-bold hover:bg-blue-700" data-testid="results-exit-btn">{copy.backHome}</Button>
    </div>
  );
}

function ResultsSummary({ session, accuracy, gaming, reasons, balance, copy }) {
  return (
    <div className="estudia-card animate-rise p-6 text-center">
      <span className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-blue-50 text-blue-600"><Trophy className="h-8 w-8" /></span>
      <h1 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900">{copy.sessionFinished}</h1>
      <p className="mt-2 text-sm text-slate-500">{session.correct_count} {copy.of} {session.answered_count} {copy.correctAnswers} · {accuracy}% {copy.accuracy}</p>
      {gaming && session.coins_awarded > 0 && (
        <div className="mt-5 rounded-2xl bg-amber-50 p-4" data-testid="coins-earned">
          <p className="flex items-center justify-center gap-2 font-display text-2xl font-extrabold text-amber-950"><Coins className="h-6 w-6" /> +{session.coins_awarded}</p>
          {reasons?.length > 0 && <ul className="mt-2 space-y-0.5 text-xs text-amber-950">{reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>}
          <p className="mt-2 text-xs font-semibold text-amber-950">{copy.balance}: {balance} {copy.coins}</p>
        </div>
      )}
    </div>
  );
}

function TopicResults({ topics, copy }) {
  return (
    <div className="estudia-card mt-5 p-6" data-testid="results-by-topic">
      <h2 className="font-display text-lg font-bold text-slate-900">{copy.mastery}</h2>
      <ul className="mt-4 space-y-4">
        {topics.map((topic) => (
          <li key={topic.topic_id} data-testid="results-topic-row">
            <div className="flex items-baseline justify-between gap-3"><p className="truncate text-sm font-semibold text-slate-800">{topic.topic}</p><span className="shrink-0 text-xs text-slate-400">{topic.correct}/{topic.asked}</span></div>
            <div className="mt-1.5"><MasteryBar value={topic.mastery} /></div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ExtendActions({ onExtend, busy, copy }) {
  const actions = [
    [10, "standard", copy.questions10, "extend-10-btn"],
    [20, "standard", copy.questions20, "extend-20-btn"],
    [10, "weak_topics", copy.reinforce, "extend-weak-btn"],
    [10, "harder", copy.harder, "extend-harder-btn"],
  ];
  return (
    <div className="mt-6 grid gap-3 sm:grid-cols-2">
      {actions.map(([additional, mode, label, testId]) => (
        <Button key={testId} onClick={() => onExtend(additional, mode)} disabled={busy} variant="outline" className="estudia-touch rounded-2xl font-bold" data-testid={testId}>{label}</Button>
      ))}
    </div>
  );
}
